import { useMemo } from "react";
import {
  EXERCISE_KIND_LABELS,
  PRIVATE_ACTIVITY_LABEL,
  isPrivateEvent,
  type ExerciseKind,
  type ExercisePayload,
} from "@medassist/shared";
import { ActivityIcon } from "../../icons.js";
import { relativeTime } from "../../../lib/time.js";
import type { WidgetProps } from "../types.js";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
const VISIBLE_RECENT = 3;

export function ActivityWidget({ events, onNavigate }: WidgetProps) {
  const weekEvents = useMemo(() => {
    const cutoff = Date.now() - SEVEN_DAYS_MS;
    return events
      .filter((e) => e.eventType === "exercise" && !e.deleted && new Date(e.occurredAt).getTime() >= cutoff)
      .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
  }, [events]);

  const totalMinutes = weekEvents.reduce(
    (sum, e) => sum + ((e.payload as Partial<ExercisePayload>).durationMin ?? 0),
    0
  );

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <div className="mb-2 flex items-center gap-2">
        <ActivityIcon size={16} className="text-(--accent)" />
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Activity</h2>
        <button onClick={() => onNavigate("activity")} className="ml-auto text-xs text-(--accent) hover:underline">
          Details
        </button>
      </div>

      {weekEvents.length === 0 ? (
        <p className="mb-3 text-sm text-gray-400 dark:text-gray-500">Nothing logged this week.</p>
      ) : (
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-2 text-gray-700 dark:text-gray-300">
            <span>This week</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {weekEvents.length} session{weekEvents.length !== 1 ? "s" : ""} · {totalMinutes} min
            </span>
          </div>
          <ul className="space-y-1">
            {weekEvents.slice(0, VISIBLE_RECENT).map((e) => {
              const p = e.payload as Partial<ExercisePayload>;
              const priv = isPrivateEvent(e);
              return (
                <li key={e.eventId} className="flex items-center justify-between text-xs">
                  <span className="text-gray-700 dark:text-gray-300">
                    {priv
                      ? `${PRIVATE_ACTIVITY_LABEL} · ${p.durationMin} min`
                      : `${EXERCISE_KIND_LABELS[p.kind as ExerciseKind] ?? p.kind} · ${p.durationMin} min`}
                  </span>
                  <span className="shrink-0 text-gray-400 dark:text-gray-500">{relativeTime(e.occurredAt)}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <button
        onClick={() => onNavigate("activity")}
        className="w-full rounded-lg border border-(--accent) py-1.5 text-xs font-medium text-(--accent) hover:bg-(--accent)/10"
      >
        Log activity
      </button>
    </section>
  );
}
